import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Search, MapPin, Minus, Plus, Sparkles, Search as SearchIcon } from 'lucide-react';
import { Calendar } from '../ui/calendar';
import { Popover, PopoverContent, PopoverTrigger, PopoverAnchor } from '../ui/popover';
import { Button } from '../ui/button';
import { useGoogleMaps } from '../../lib/googleMaps';
import { searchLocations } from './locationsData';
import { recordSearch } from '../../lib/recent';

const today = (offsetDays = 0) => {
  const d = new Date();
  d.setDate(d.getDate() + offsetDays);
  d.setHours(0, 0, 0, 0);
  return d;
};
const toISO = (d) => (d instanceof Date ? d.toISOString().slice(0, 10) : '');
const fmtShort = (d) => (d ? d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' }) : '');
const weekday = (d) => (d ? d.toLocaleDateString('en-IN', { weekday: 'long' }) : '');

// ──────────────────────────────────────────────────────────────────────────
// Google Places-powered worldwide location autocomplete (MMT-style suggestions)
// Falls back to a curated local list when Google API key isn't ready or fails.
// ──────────────────────────────────────────────────────────────────────────
const LocationInput = ({ value, onSelect, compact = false }) => {
  const [query, setQuery] = useState(value || '');
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const [googleSuggestions, setGoogleSuggestions] = useState([]);
  const wrapperRef = useRef(null);
  const sessionTokenRef = useRef(null);
  const debounceRef = useRef(null);
  const { isLoaded } = useGoogleMaps();

  useEffect(() => setQuery(value || ''), [value]);

  // Initialise the Places session token once Maps is loaded
  useEffect(() => {
    if (!isLoaded || !window.google?.maps?.places) return;
    try {
      sessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken();
    } catch {
      // older SDK
    }
  }, [isLoaded]);

  // Live worldwide lookup (debounced). Tries Google Places (New) first; falls back
  // to Photon (Komoot, free OSM-backed) which works without an API key.
  useEffect(() => {
    const q = (query || '').trim();
    if (!q) { setGoogleSuggestions([]); return; }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      // 1. Try Google Places (New) if available
      if (window.google?.maps?.places?.AutocompleteSuggestion) {
        try {
          const { suggestions } = await window.google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
            input: q,
            sessionToken: sessionTokenRef.current,
          });
          const mapped = (suggestions || []).slice(0, 8).map((s) => {
            const p = s.placePrediction;
            if (!p) return null;
            const types = p.types || [];
            const isCity = types.includes('locality') || types.includes('administrative_area_level_1') || types.includes('country');
            return {
              name: p.mainText?.text || p.text?.text || '',
              subtitle: p.secondaryText?.text || '',
              type: isCity ? 'city' : 'area',
              source: 'google',
              placeId: p.placeId,
            };
          }).filter((s) => s && s.name);
          if (mapped.length) { setGoogleSuggestions(mapped); return; }
        } catch {
          // fall through to Photon
        }
      }
      // 2. Photon (Komoot) — free worldwide OSM search, no key required
      try {
        const resp = await fetch(`https://photon.komoot.io/api?q=${encodeURIComponent(q)}&limit=8&lang=en`);
        if (!resp.ok) throw new Error('photon');
        const data = await resp.json();
        const mapped = (data.features || []).slice(0, 8).map((f) => {
          const pr = f.properties || {};
          const parts = [pr.city, pr.state, pr.country].filter(Boolean);
          const subtitle = parts.length ? parts.join(', ') : (pr.osm_value || '');
          const isCity = pr.osm_value === 'city' || pr.osm_value === 'town' || pr.osm_value === 'village' || pr.type === 'city';
          const [lng, lat] = (f.geometry && f.geometry.coordinates) || [null, null];
          return {
            name: pr.name || subtitle || q,
            subtitle: subtitle || 'Worldwide',
            type: isCity ? 'city' : 'area',
            source: 'photon',
            placeId: `osm-${pr.osm_id}`,
            lat,
            lng,
          };
        }).filter((s) => s.name);
        setGoogleSuggestions(mapped);
      } catch {
        setGoogleSuggestions([]);
      }
    }, 200);
    return () => clearTimeout(debounceRef.current);
  }, [query]);

  const localSuggestions = useMemo(() => searchLocations(query, 4), [query]);

  // Merge Google (worldwide) first, then local curated — dedupe by name
  const suggestions = useMemo(() => {
    const merged = [...googleSuggestions];
    localSuggestions.forEach((s) => {
      if (!merged.some((g) => g.name.toLowerCase() === s.name.toLowerCase())) merged.push(s);
    });
    return merged.slice(0, 8);
  }, [googleSuggestions, localSuggestions]);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const pickSuggestion = (loc) => {
    setQuery(loc.name);
    onSelect(loc.name, loc);
    setOpen(false);
    // Refresh session token after a selection (Google Places billing best-practice)
    if (window.google?.maps?.places) {
      sessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken();
    }
  };

  const handleKeyDown = (e) => {
    if (!open) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setHighlight((h) => Math.min(h + 1, suggestions.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlight((h) => Math.max(h - 1, 0)); }
    else if (e.key === 'Enter' && suggestions[highlight]) { e.preventDefault(); pickSuggestion(suggestions[highlight]); }
    else if (e.key === 'Escape') setOpen(false);
  };

  return (
    <div ref={wrapperRef} className="relative w-full">
      {compact ? (
        <>
          <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 leading-tight">City, Area Or Property</p>
          <input
            data-testid="hotels-city"
            value={query}
            placeholder="Search any city or area worldwide"
            onFocus={() => setOpen(true)}
            onChange={(e) => { setQuery(e.target.value); setOpen(true); setHighlight(0); onSelect(e.target.value); }}
            onKeyDown={handleKeyDown}
            className="w-full bg-transparent outline-none text-sm font-bold text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-500 placeholder:font-normal placeholder:text-xs leading-tight tracking-tight"
          />
        </>
      ) : (
        <>
          <p className="text-xs text-gray-600 dark:text-gray-400 mb-1 font-normal">City, Property Name Or Location</p>
          <input
            data-testid="hotels-city"
            value={query}
            placeholder="Search any city or area worldwide"
            onFocus={() => setOpen(true)}
            onChange={(e) => { setQuery(e.target.value); setOpen(true); setHighlight(0); onSelect(e.target.value); }}
            onKeyDown={handleKeyDown}
            className="w-full bg-transparent outline-none text-3xl font-black text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-500 placeholder:font-normal placeholder:text-base leading-tight tracking-tight"
          />
          {query && suggestions[0]?.subtitle && (
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5 truncate">{suggestions[0].subtitle}</p>
          )}
        </>
      )}

      {open && (
        <div
          data-testid="location-suggestions"
          className="absolute top-full left-0 right-[-50px] mt-2 bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 shadow-2xl z-50 max-h-[420px] overflow-y-auto"
        >
          {query && (
            <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-800 text-sm text-gray-700 dark:text-gray-300 flex items-center justify-between bg-gray-50 dark:bg-gray-800">
              <span className="flex items-center gap-2"><SearchIcon className="h-4 w-4 text-gray-400 dark:text-gray-500" /> {query}</span>
              <button className="text-xs text-gray-500 dark:text-gray-400 hover:text-red-600 font-semibold tracking-wide" onClick={() => { setQuery(''); onSelect(''); setGoogleSuggestions([]); }}>CLEAR</button>
            </div>
          )}

          {suggestions.length === 0 && query && (
            <div className="px-4 py-6 text-center text-sm text-gray-500 dark:text-gray-400">
              <Sparkles className="h-5 w-5 mx-auto mb-1 text-gray-400 dark:text-gray-500" />
              {isLoaded ? 'No matching places found.' : 'Loading worldwide search…'}
            </div>
          )}

          {suggestions.map((s, i) => (
            <button
              type="button"
              key={`${s.placeId || s.name}-${i}`}
              data-testid={`suggestion-${s.name.replace(/\s+/g, '-').toLowerCase()}`}
              onClick={() => pickSuggestion(s)}
              onMouseEnter={() => setHighlight(i)}
              className={`w-full text-left px-4 py-3 flex items-start gap-3 border-b border-gray-50 dark:border-gray-800 last:border-0 transition-colors ${highlight === i ? 'bg-red-50 dark:bg-red-900/30' : 'hover:bg-gray-50 dark:hover:bg-gray-800'}`}
            >
              <div className="mt-0.5">
                {s.type === 'city' ? (
                  <div className="bg-gray-100 dark:bg-gray-800 rounded-full p-1.5"><MapPin className="h-4 w-4 text-gray-600 dark:text-gray-300" /></div>
                ) : (
                  <div className="bg-red-50 dark:bg-red-900/40 rounded-full p-1.5"><MapPin className="h-4 w-4 text-red-600 dark:text-red-300" /></div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-gray-900 dark:text-white truncate text-[15px]">
                  <Highlight text={s.name} query={query} />
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">{s.subtitle}</p>
              </div>
            </button>
          ))}

          {!query && (
            <div className="px-4 py-6 text-sm text-gray-500 dark:text-gray-400 text-center">
              <Search className="h-5 w-5 mx-auto mb-2 text-gray-400 dark:text-gray-500" />
              Start typing — search any city or area, worldwide.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const Highlight = ({ text, query }) => {
  const q = (query || '').trim();
  if (!q) return text;
  const idx = text.toLowerCase().indexOf(q.toLowerCase());
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <span className="font-bold text-gray-900 dark:text-white">{text.slice(idx, idx + q.length)}</span>
      {text.slice(idx + q.length)}
    </>
  );
};

// ──────────────────────────────────────────────────────────────────────────
// Combined Check-in / Check-out range picker — MMT-style big bold date
// ──────────────────────────────────────────────────────────────────────────
const DatePill = ({ label, date, active, onClick, testId, compact = false }) => {
  // MMT compact format: 2 lines only. "CHECK-IN" + "Mon, 8 Jun 2026"
  if (compact) {
    const fmt = (d) => d ? `${d.toLocaleDateString('en-IN', { weekday: 'short' })}, ${d.getDate()} ${d.toLocaleDateString('en-IN', { month: 'short' })} ${d.getFullYear()}` : 'Pick date';
    return (
      <div
        data-testid={testId}
        onClick={onClick}
        className={`px-3 py-1 cursor-pointer transition-colors ${active ? 'bg-red-50 dark:bg-red-900/30' : 'hover:bg-gray-50 dark:hover:bg-gray-800'}`}
      >
        <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 leading-tight">{label}</p>
        <p className={`text-sm font-bold leading-tight ${date ? 'text-gray-900 dark:text-white' : 'text-gray-400'}`}>{fmt(date)}</p>
      </div>
    );
  }
  return (
    <div
      data-testid={testId}
      onClick={onClick}
      className={`px-4 py-3 cursor-pointer transition-colors ${active ? 'bg-red-50 dark:bg-red-900/30' : 'hover:bg-gray-50 dark:hover:bg-gray-800'}`}
    >
      <p className="text-xs text-gray-600 dark:text-gray-400 mb-1 font-normal">{label}</p>
      {date ? (
        <>
          <p className="leading-none mb-1">
            <span className="text-3xl font-black text-gray-900 dark:text-white tracking-tight">{date.getDate()}</span>
            <span className="text-base font-semibold text-gray-700 dark:text-gray-300 ml-1">{date.toLocaleDateString('en-IN', { month: 'short' })}'{date.toLocaleDateString('en-IN', { year: '2-digit' })}</span>
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-400">{weekday(date)}</p>
        </>
      ) : (
        <p className="text-2xl font-black text-gray-400 dark:text-gray-500 tracking-tight">Pick date</p>
      )}
    </div>
  );
};

const DatesPanel = ({ checkIn, checkOut, onChange, focus = 'checkIn', compact = false }) => {
  const [open, setOpen] = useState(false);
  const [internalFocus, setInternalFocus] = useState(focus);

  const range = { from: checkIn, to: checkOut };
  // Bypass react-day-picker's built-in range logic (it ignores clicks "inside" an
  // existing range, e.g. picking 5 Jun when range is 4→6 Jun). We control the
  // dates ourselves based on which pill (Check-in / Check-out) the user opened.
  const handleDayClick = (day) => {
    if (!day) return;
    const d = new Date(day);
    d.setHours(0, 0, 0, 0);
    if (internalFocus === 'checkIn') {
      // Always update check-in to the clicked day.
      let nextOut = checkOut;
      if (!nextOut || nextOut <= d) {
        nextOut = new Date(d.getTime() + 24 * 3600 * 1000);
      }
      onChange({ checkIn: d, checkOut: nextOut });
      setInternalFocus('checkOut');
    } else {
      // Check-out focus.
      if (!checkIn || d <= checkIn) {
        // User picked a day before/equal to current check-in → treat it as a new check-in.
        onChange({ checkIn: d, checkOut: null });
        setInternalFocus('checkOut');
        return;
      }
      onChange({ checkIn, checkOut: d });
      setInternalFocus('checkIn');
      setTimeout(() => setOpen(false), 200);
    }
  };

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) setInternalFocus(focus); }}>
      <PopoverAnchor asChild>
        <div
          data-testid="hotels-checkin-card"
          onClick={(e) => { e.stopPropagation(); setOpen(true); setInternalFocus('checkIn'); }}
          className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 hover:border-red-400 transition-colors overflow-hidden cursor-pointer"
        >
          <DatePill label="Check-in" date={checkIn} active={open && internalFocus === 'checkIn'} testId="hotels-checkin-trigger" compact={compact} onClick={(e) => { e.stopPropagation(); setOpen(true); setInternalFocus('checkIn'); }} />
        </div>
      </PopoverAnchor>
      <div
        data-testid="hotels-checkout-card"
        onClick={(e) => { e.stopPropagation(); setOpen(true); setInternalFocus('checkOut'); }}
        className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 hover:border-red-400 transition-colors overflow-hidden cursor-pointer"
      >
        <DatePill label="Check-out" date={checkOut} active={open && internalFocus === 'checkOut'} testId="hotels-checkout-trigger" compact={compact} onClick={(e) => { e.stopPropagation(); setOpen(true); setInternalFocus('checkOut'); }} />
      </div>
      <PopoverContent align="start" className="p-0 w-auto border border-gray-200 dark:border-gray-700 shadow-2xl" data-testid="hotels-dates-popover">
        <div className="bg-white dark:bg-gray-900 rounded-lg overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between">
            <p className="text-sm font-semibold text-gray-900 dark:text-white">{internalFocus === 'checkIn' ? 'Select Check-in date' : 'Select Check-out date'}</p>
            {checkIn && checkOut && (
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {fmtShort(checkIn)} → {fmtShort(checkOut)} · {Math.max(1, Math.round((checkOut - checkIn) / (1000 * 60 * 60 * 24)))} night(s)
              </p>
            )}
          </div>
          <Calendar mode="range" numberOfMonths={2} selected={range} onDayClick={handleDayClick} disabled={(d) => d < today(0)} defaultMonth={checkIn || today(0)} />
          <div className="px-4 py-3 border-t border-gray-100 dark:border-gray-800 flex justify-end">
            <Button type="button" onClick={() => setOpen(false)} disabled={!checkIn || !checkOut} data-testid="dates-apply" className="bg-red-600 hover:bg-red-700 text-white px-6">Apply</Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

// ──────────────────────────────────────────────────────────────────────────
// Combined Rooms & Guests popover
// ──────────────────────────────────────────────────────────────────────────
const RoomsGuestsPanel = ({ rooms, adults, children, childAges = [], onChange, compact = false }) => {
  const [open, setOpen] = useState(false);
  const Counter = ({ label, sublabel, value, onDec, onInc, testIdBase }) => (
    <div className="flex items-center justify-between py-3">
      <div>
        <p className="font-semibold text-gray-900 dark:text-white">{label}</p>
        {sublabel && <p className="text-xs text-gray-500 dark:text-gray-400">{sublabel}</p>}
      </div>
      <div className="flex items-center gap-3 border border-gray-300 dark:border-gray-700 rounded-lg px-2 py-1">
        <button type="button" onClick={onDec} className="text-gray-700 dark:text-gray-200 hover:text-red-600 disabled:opacity-30" data-testid={`${testIdBase}-dec`}><Minus className="h-4 w-4" /></button>
        <span className="font-bold text-gray-900 dark:text-white w-6 text-center" data-testid={`${testIdBase}-value`}>{value}</span>
        <button type="button" onClick={onInc} className="text-gray-700 dark:text-gray-200 hover:text-red-600" data-testid={`${testIdBase}-inc`}><Plus className="h-4 w-4" /></button>
      </div>
    </div>
  );

  // Keep childAges length in sync with `children` (pad/truncate, default age 8).
  const syncAges = (n) => {
    if (n === childAges.length) return childAges;
    if (n < childAges.length) return childAges.slice(0, n);
    return [...childAges, ...Array(n - childAges.length).fill(8)];
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <div data-testid="hotels-roomsguests-trigger" className={`${compact ? 'px-3 py-1' : 'px-4 py-3'} hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors cursor-pointer h-full`}>
          {compact ? (
            <>
              <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 leading-tight">Rooms &amp; Guests</p>
              <p className="text-sm font-bold leading-tight text-gray-900 dark:text-white">
                {rooms} Room{rooms > 1 ? 's' : ''}, {adults} Adult{adults > 1 ? 's' : ''}{children > 0 ? `, ${children} Child${children > 1 ? 'ren' : ''}` : ''}
              </p>
            </>
          ) : (
            <>
              <p className="text-xs text-gray-600 dark:text-gray-400 mb-1 font-normal">Rooms &amp; Guests</p>
              <p className="leading-none mb-1">
                <span className="text-3xl font-black text-gray-900 dark:text-white tracking-tight">{rooms}</span>
                <span className="text-base font-semibold text-gray-700 dark:text-gray-300 ml-1">Room{rooms > 1 ? 's' : ''}</span>
                <span className="text-3xl font-black text-gray-900 dark:text-white tracking-tight ml-2">{adults}</span>
                <span className="text-base font-semibold text-gray-700 dark:text-gray-300 ml-1">Adult{adults > 1 ? 's' : ''}</span>
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">{children > 0 ? `${children} Child${children > 1 ? 'ren' : ''}` : 'No children'}</p>
            </>
          )}
        </div>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0 border border-gray-200 dark:border-gray-700 shadow-2xl" data-testid="hotels-roomsguests-popover">
        <div className="bg-white dark:bg-gray-900 rounded-lg overflow-hidden">
          <div className="px-5 py-2 divide-y divide-gray-100 dark:divide-gray-800">
            <Counter label="Rooms" value={rooms} onDec={() => onChange({ rooms: Math.max(1, rooms - 1), adults, children, childAges })} onInc={() => onChange({ rooms: Math.min(5, rooms + 1), adults, children, childAges })} testIdBase="rooms" />
            <Counter label="Adults" value={adults} onDec={() => onChange({ rooms, adults: Math.max(1, adults - 1), children, childAges })} onInc={() => onChange({ rooms, adults: Math.min(10, adults + 1), children, childAges })} testIdBase="adults" />
            <Counter
              label="Children" sublabel="0 - 17 years old" value={children}
              onDec={() => { const n = Math.max(0, children - 1); onChange({ rooms, adults, children: n, childAges: syncAges(n) }); }}
              onInc={() => { const n = Math.min(6, children + 1); onChange({ rooms, adults, children: n, childAges: syncAges(n) }); }}
              testIdBase="children"
            />
            {children > 0 && (
              <div className="py-3">
                <p className="text-xs text-gray-600 dark:text-gray-400 mb-2 font-semibold">Age of each child at check-in</p>
                <div className="grid grid-cols-3 gap-2">
                  {Array.from({ length: children }).map((_, i) => (
                    <select
                      key={i}
                      data-testid={`child-age-${i}`}
                      value={childAges[i] ?? 8}
                      onChange={(e) => {
                        const next = [...syncAges(children)];
                        next[i] = parseInt(e.target.value, 10);
                        onChange({ rooms, adults, children, childAges: next });
                      }}
                      className="h-9 text-sm border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-md px-2"
                    >
                      {Array.from({ length: 18 }).map((_, age) => (
                        <option key={age} value={age}>{age === 0 ? '< 1 yr' : `${age} yr`}</option>
                      ))}
                    </select>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div className="px-5 py-3 border-t border-gray-100 dark:border-gray-800 flex justify-end">
            <Button type="button" onClick={() => setOpen(false)} className="bg-red-600 hover:bg-red-700 text-white px-6" data-testid="roomsguests-apply">Apply</Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

// ──────────────────────────────────────────────────────────────────────────
// Main hotel search panel — MMT-style layout, Elite Travo red accents
// ──────────────────────────────────────────────────────────────────────────
export { LocationInput, DatesPanel, RoomsGuestsPanel };

const HotelSearchPanel = ({ initialValues, onSubmit, buttonLabel = 'SEARCH', compact = false }) => {
  const navigate = useNavigate();
  const [city, setCity] = useState(initialValues?.city || '');
  const [coords, setCoords] = useState({ lat: initialValues?.lat || null, lng: initialValues?.lng || null });
  const [dates, setDates] = useState({
    checkIn: initialValues?.checkIn || today(7),
    checkOut: initialValues?.checkOut || today(9),
  });
  const [guests, setGuests] = useState({
    rooms: initialValues?.rooms || 1,
    adults: initialValues?.adults || 2,
    children: initialValues?.children || 0,
    childAges: initialValues?.childAges || [],
  });

  useEffect(() => {
    if (initialValues) {
      setCity(initialValues.city || '');
      setCoords({ lat: initialValues.lat || null, lng: initialValues.lng || null });
      setDates({ checkIn: initialValues.checkIn || today(7), checkOut: initialValues.checkOut || today(9) });
      setGuests({ rooms: initialValues.rooms || 1, adults: initialValues.adults || 2, children: initialValues.children || 0, childAges: initialValues.childAges || [] });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues?.city, initialValues?.checkIn?.getTime?.(), initialValues?.checkOut?.getTime?.(), initialValues?.rooms, initialValues?.adults, initialValues?.children]);

  const submit = () => {
    if (!city || !city.trim()) {
      toast.error('Please enter a city, area or property to search', { duration: 3500 });
      return;
    }
    const payload = {
      city,
      lat: coords.lat,
      lng: coords.lng,
      check_in: toISO(dates.checkIn),
      check_out: toISO(dates.checkOut),
      rooms: guests.rooms,
      guests: guests.adults + guests.children,
      adults: guests.adults,
      children: guests.children,
      child_ages: guests.childAges,
    };
    if (onSubmit) { onSubmit(payload); return; }
    const params = new URLSearchParams();
    if (payload.city) params.set('city', payload.city);
    if (payload.lat != null) params.set('lat', String(payload.lat));
    if (payload.lng != null) params.set('lng', String(payload.lng));
    if (payload.check_in) params.set('check_in', payload.check_in);
    if (payload.check_out) params.set('check_out', payload.check_out);
    params.set('rooms', String(payload.rooms));
    params.set('guests', String(payload.guests));
    if (payload.children > 0) {
      params.set('children', String(payload.children));
      params.set('child_ages', payload.child_ages.join(','));
    }
    const url = `/hotels/search?${params.toString()}`;
    if (payload.city) {
      recordSearch({
        module: 'hotels',
        label: payload.city,
        sublabel: `${payload.check_in} → ${payload.check_out} · ${payload.guests || 2} guests`,
        url,
      });
    }
    navigate(url);
  };

  const onCitySelect = (name, loc) => {
    setCity(name);
    if (loc?.lat != null && loc?.lng != null) {
      setCoords({ lat: loc.lat, lng: loc.lng });
    }
  };

  return (
    <div className={`${compact ? 'text-sm' : ''}`}>
      <div className="grid grid-cols-1 md:grid-cols-[2fr_1fr_1fr_1.4fr_auto] items-stretch gap-2">
        {/* Location */}
        <div className={`rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 hover:border-red-400 transition-colors overflow-visible ${compact ? 'px-3 py-1' : 'px-4 py-3'}`}>
          <LocationInput value={city} onSelect={onCitySelect} compact={compact} />
        </div>

        {/* Check-in & Check-out — DatesPanel renders TWO independent cards as siblings */}
        <DatesPanel checkIn={dates.checkIn} checkOut={dates.checkOut} onChange={setDates} compact={compact} />

        {/* Rooms & Guests */}
        <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 hover:border-red-400 transition-colors overflow-hidden">
          <RoomsGuestsPanel rooms={guests.rooms} adults={guests.adults} children={guests.children} childAges={guests.childAges} onChange={setGuests} compact={compact} />
        </div>

        {/* Search */}
        <button
          data-testid="search-hotels-btn"
          onClick={submit}
          className={`bg-gradient-to-br from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-bold flex items-center justify-center gap-2 transition-all shadow-md rounded-xl tracking-wider ${compact ? 'text-sm px-5 py-2 md:py-0' : 'text-lg px-10 py-5 md:py-0'}`}
        >
          <Search className={compact ? 'h-4 w-4' : 'h-5 w-5'} />
          {buttonLabel}
        </button>
      </div>
    </div>
  );
};

export default HotelSearchPanel;
